import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2 } from 'lucide-react';

const WhyTweExists = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Blog</span>
        </Link>

        {/* Article Header */}
        <article>
          <header className="mb-12">
            <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-500 text-sm font-semibold rounded-full border border-orange-500/30 mb-4">
              Mission
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Why Twe Exists
            </h1>
            <div className="flex items-center space-x-6 text-gray-400 mb-8">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>Tu Dang (Tusdang)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>January 15, 2025</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>8 min read</span>
              </div>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 mb-8">
              <p className="text-2xl text-gray-300 leading-relaxed italic">
                Technology was meant to empower people. Somewhere along the way, it became complicated, fragmented, and exclusionary.
              </p>
              <p className="text-2xl text-orange-500 font-semibold mt-4">
                Twe exists to fix that.
              </p>
            </div>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">The Core Problem: Power Without Accessibility</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                AI has become incredibly powerful — yet for most people, it remains indirect, slow, and difficult to use.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Today's tools still require:
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li>constant typing,</li>
                <li>repetitive clicks,</li>
                <li>complex interfaces,</li>
                <li>and endless switching between apps.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong className="text-white">For power users, this creates friction.</strong>
                <br />
                <strong className="text-white">For people with disabilities, it creates barriers.</strong>
              </p>
              <p className="text-xl text-orange-400 font-semibold">
                The result? Technology that could help millions instead leaves them behind.
              </p>
              <p className="text-xl text-white font-semibold mt-4">
                Twe exists because that is unacceptable.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Twe's Mission: From Intent to Action</h2>
              <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-8 mb-6">
                <p className="text-2xl text-white font-semibold mb-4">
                  Twe was built around one fundamental idea:
                </p>
                <p className="text-2xl text-orange-500 font-bold">
                  Your computer should respond to human intent — not demand technical effort.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Whether that intent is spoken, simple, or complex, Twe turns it into immediate system-level action.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>No learning curve.</li>
                <li>No complicated UI.</li>
                <li>No physical strain.</li>
                <li className="text-white font-semibold">Just results.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Why Voice Is Not a Feature — It's a Necessity</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Voice interaction isn't a trend. It's the most human interface ever created.
              </p>
              <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 mb-6">
                <p className="text-lg text-white font-semibold mb-4">For:</p>
                <ul className="space-y-2 text-gray-300">
                  <li>people with mobility limitations,</li>
                  <li>users with visual impairments,</li>
                  <li>individuals with repetitive strain injuries,</li>
                  <li>neurodivergent users who think faster than they type,</li>
                </ul>
                <p className="text-xl text-orange-500 font-bold mt-4">
                  voice is not convenience — it's freedom.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Twe uses real-time voice AI to allow users to control applications, manage workflows, automate daily tasks, and interact with their system naturally.
              </p>
              <p className="text-xl text-white font-semibold mt-4">
                Your voice becomes your interface.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Why Twe Lives on the Desktop</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Browsers are limited. Accessibility should not be.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Twe is a native desktop AI assistant because real empowerment requires:
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li>full system control,</li>
                <li>direct OS interaction,</li>
                <li>window and app management,</li>
                <li>file access and automation.</li>
              </ul>
              <p className="text-xl text-orange-500 font-semibold">
                This makes Twe not just an AI assistant — but a personal digital operator.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Built for People Who Value Leverage</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Twe exists for those who need technology to work for them:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-500 mb-3">🔹 Founders & Entrepreneurs</h3>
                  <p className="text-gray-300">Who need speed, clarity, and execution without distractions.</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-500 mb-3">🔹 Developers & Technical Creators</h3>
                  <p className="text-gray-300">Who want to stay in flow and automate repetitive system tasks.</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-500 mb-3">🔹 Content Creators & Professionals</h3>
                  <p className="text-gray-300">Who juggle multiple tools, platforms, and ideas daily.</p>
                </div>
                <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-500 mb-3">🔹 People with Disabilities</h3>
                  <p className="text-gray-300">Who deserve equal access to technology, productivity, and independence — without compromise.</p>
                </div>
              </div>
              <p className="text-xl text-white font-semibold mt-6">
                Twe does not treat accessibility as an afterthought. It treats it as a design principle.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Security, Responsibility, and Trust</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Twe was built as real software — not a demo.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                That means:
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li>secure authentication,</li>
                <li>transparent usage limits,</li>
                <li>responsible AI cost handling,</li>
                <li>and user-first data practices.</li>
              </ul>
              <p className="text-xl text-orange-500 font-semibold">
                Because people don't just use Twe — they trust it.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">The Founder's Perspective</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Twe was created by Tu Dang (Tusdang) — a young founder who learned early that:
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li>systems matter more than motivation,</li>
                <li>leverage beats effort,</li>
                <li>and the right tools change lives.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Having navigated pressure, limited resources, and real-world constraints, he built Twe not as a concept — but as a solution he personally needed. One that could help others go further, faster, and freer.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-white mb-6">Why Twe Truly Exists</h2>
              <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-8">
                <p className="text-xl text-white font-semibold mb-4">
                  Twe exists to:
                </p>
                <ul className="space-y-3 text-lg text-gray-300">
                  <li>reduce friction,</li>
                  <li>remove barriers,</li>
                  <li>unlock human potential,</li>
                  <li>and make powerful technology accessible to everyone.</li>
                </ul>
                <p className="text-2xl text-orange-500 font-bold mt-6">
                  Not by asking people to adapt to machines — but by making machines finally adapt to people.
                </p>
                <p className="text-xl text-white font-semibold mt-4">
                  That is the future Twe is building.
                </p>
              </div>
            </section>
          </div>

          {/* Share & Navigate */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <div className="flex items-center justify-between">
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-orange-500 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Blog</span>
              </Link>
              <button className="inline-flex items-center space-x-2 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors">
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>
          </div>

          {/* Next Article */}
          <div className="mt-12 bg-zinc-900/50 border border-white/5 rounded-xl p-6">
            <p className="text-sm text-gray-400 mb-3">Next Article</p>
            <Link
              to="/blog/about-the-journey"
              className="text-2xl font-bold text-white hover:text-orange-500 transition-colors"
            >
              About the Journey →
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default WhyTweExists;